import { directionFor, isJaSource, type Direction, type OtherLanguage, otherLanguageOf } from "./translateLanguages";

export type DetectedLanguage = "ja" | OtherLanguage;

type LatinLanguage = "en" | "pt" | "es" | "fr" | "de" | "nl" | "it";
type Scores = Record<LatinLanguage, number>;

const STRONG = 3;
const COMMON = 1;
const MIN_SCORE = 2;
const MIN_LEAD = 2;

const KANA = /[぀-ヿｦ-ﾟ]/g;
const HANGUL = /[ᄀ-ᇿ㄰-㆏가-힯]/g;
const CYRILLIC = /[Ѐ-ӿ]/g;
const ARABIC = /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/g;
const HAN = /[㐀-䶿一-鿿]/g;
const LATIN = /[A-Za-zÀ-ɏ]/g;
const LATIN_TOKEN = /[a-zß-ɏ]+(?:['’][a-zß-ɏ]+)*/g;

const SIMPLIFIED_ONLY = "们这个么吗说为还时现问题话电马车门间见开关东书买卖从发对过长样谁让请谢爱钱热";

// Characters that point at one language (or a short list of them). Only the
// unmistakable ones (ã, ñ, ß, ...) carry enough weight to decide on their own.
const CHAR_HINTS: Record<string, Partial<Scores>> = {
    ã: { pt: STRONG },
    õ: { pt: STRONG },
    ñ: { es: STRONG },
    "¿": { es: STRONG },
    "¡": { es: STRONG },
    ß: { de: STRONG },
    œ: { fr: STRONG },
    ç: { pt: COMMON, fr: COMMON },
    ä: { de: COMMON },
    ö: { de: COMMON },
    ü: { de: COMMON },
    ë: { nl: COMMON },
    ï: { nl: COMMON },
    ê: { pt: COMMON, fr: COMMON },
    ô: { pt: COMMON, fr: COMMON },
    â: { pt: COMMON, fr: COMMON },
    è: { fr: COMMON, it: COMMON },
    ù: { fr: COMMON, it: COMMON },
    ì: { it: COMMON },
    ò: { it: COMMON },
    à: { pt: COMMON, fr: COMMON, it: COMMON },
    î: { fr: COMMON },
    û: { fr: COMMON },
    á: { es: COMMON, pt: COMMON },
    í: { es: COMMON, pt: COMMON },
    ó: { es: COMMON, pt: COMMON },
    ú: { es: COMMON, pt: COMMON },
};

const FRENCH_ELISION = /^(?:j|qu|n|m|t|s|c|jusqu|lorsqu|puisqu)['’]/;

// Short lists of very frequent words per language. `strong` words (greetings
// and function words that exist in only one of these languages) decide a
// short text on their own; `common` ones need a second hit. A word listed for
// several languages simply scores for each of them, so shared words cancel out.
const LEXICON: Record<LatinLanguage, { strong: string[]; common: string[] }> = {
    en: {
        strong: ["hello", "thanks", "thank", "please", "goodbye", "welcome", "sorry", "the", "you", "with", "what", "where", "when", "why", "who", "how", "they", "there", "this", "that", "have", "your", "and"],
        common: ["hi", "it", "be", "not", "for", "of", "from", "we", "she", "can", "good", "morning", "night", "afternoon", "evening", "yes", "bye", "my", "i'm", "it's", "don't", "can't", "you're", "that's", "isn't", "doesn't", "its", "an", "at", "by", "or", "if", "but", "all", "just", "very", "more", "some", "about", "into", "has", "had", "does", "did", "were", "his", "her", "him", "our", "their", "them", "these", "those", "which", "than", "then", "also", "only", "over", "after", "before", "because", "could", "should", "would", "are", "was"],
    },
    pt: {
        strong: ["olá", "obrigado", "obrigada", "você", "vocês", "não", "também", "então", "saudade", "tchau", "desculpe"],
        common: ["eu", "ele", "ela", "nós", "eles", "elas", "meu", "minha", "seu", "sua", "isso", "isto", "esse", "essa", "aqui", "onde", "como", "quando", "uma", "um", "uns", "umas", "com", "para", "por", "muito", "muita", "ainda", "já", "hoje", "amanhã", "ontem", "agora", "tenho", "tem", "têm", "estou", "está", "estão", "sou", "somos", "foi", "vou", "quero", "gosto", "fazer", "bom", "boa", "dia", "noite", "tudo", "bem", "casa", "tempo", "ao", "aos", "pelo", "pela", "nem", "mas", "mais", "menos", "sobre", "depois", "antes", "sempre", "nunca", "sim", "quem", "qual", "pouco", "ser", "estar", "ter", "comer", "beber", "falar", "português", "favor", "comboio", "muitos", "muitas", "todos", "todas", "nada", "algo"],
    },
    es: {
        strong: ["hola", "gracias", "buenos", "buenas", "señor", "señora", "adiós", "usted", "ustedes", "qué", "cómo", "dónde", "cuándo", "quién", "también", "mañana", "español", "gusta", "gustan"],
        common: ["yo", "tú", "él", "ella", "nosotros", "ellos", "mi", "tu", "su", "soy", "eres", "es", "son", "estoy", "estás", "está", "están", "hay", "tengo", "tiene", "quiero", "quiere", "puedo", "puede", "hacer", "el", "los", "las", "con", "para", "por", "pero", "porque", "muy", "mucho", "mucha", "sin", "sobre", "entre", "hoy", "ayer", "ahora", "siempre", "nunca", "aquí", "ahí", "esto", "eso", "este", "esta", "ese", "esa", "más", "año", "años", "día", "días", "casa", "tiempo", "cosa", "bien", "mal", "sí", "como", "donde", "comer", "beber", "hablar", "una", "un", "unos", "unas", "del", "al", "necesito", "favor", "vamos", "vez", "todo", "todos", "todas", "algo", "nada"],
    },
    fr: {
        strong: ["bonjour", "merci", "salut", "bonsoir", "s'il", "très", "c'est", "qu'est", "n'est", "oui", "français", "aujourd'hui", "ça", "être", "déjà", "où", "bonne"],
        common: ["je", "il", "elle", "nous", "vous", "ils", "elles", "est", "sont", "suis", "êtes", "ai", "avez", "avons", "pas", "ne", "ce", "cette", "ces", "mon", "ma", "mes", "ton", "ta", "tes", "son", "sa", "ses", "notre", "votre", "leur", "le", "la", "les", "des", "du", "dans", "avec", "pour", "sans", "sur", "sous", "mais", "donc", "car", "parce", "aussi", "comme", "bien", "ici", "là", "demain", "hier", "maintenant", "toujours", "jamais", "encore", "plus", "moins", "beaucoup", "peu", "tout", "tous", "toute", "rien", "chose", "jour", "nuit", "temps", "maison", "manger", "boire", "parler"],
    },
    de: {
        strong: ["danke", "bitte", "guten", "nicht", "ich", "und", "auch", "ist", "wir", "möchte", "schön", "für", "über", "tschüss", "deutsch"],
        common: ["du", "er", "sie", "ihr", "bin", "bist", "sind", "war", "habe", "hat", "haben", "kein", "keine", "oder", "aber", "noch", "schon", "sehr", "ja", "nein", "der", "die", "das", "den", "dem", "ein", "eine", "einen", "einem", "zu", "mit", "von", "auf", "aus", "bei", "nach", "wie", "was", "wo", "wann", "warum", "wer", "wird", "werden", "kann", "können", "gut", "heute", "morgen", "gestern", "jetzt", "immer", "nie", "hier", "dort", "mein", "dein", "sein", "unser", "tag", "abend", "nacht", "wasser", "haus", "essen", "trinken", "sprechen"],
    },
    nl: {
        strong: ["hoi", "dank", "dankjewel", "bedankt", "alsjeblieft", "goedemorgen", "goedemiddag", "goedenavond", "ik", "niet", "geen", "ook", "wij", "jullie", "nederlands", "mijn", "erg", "graag", "gezellig"],
        common: ["jij", "je", "hij", "zij", "we", "het", "een", "is", "zijn", "ben", "bent", "heb", "heeft", "hebben", "of", "maar", "nog", "al", "heel", "ja", "nee", "van", "voor", "met", "op", "aan", "uit", "bij", "naar", "over", "onder", "hoe", "wat", "waar", "wanneer", "waarom", "wie", "wordt", "worden", "kan", "kunnen", "wil", "willen", "goed", "vandaag", "morgen", "gisteren", "nu", "altijd", "nooit", "hier", "daar", "jouw", "ons", "onze", "dag", "avond", "nacht", "water", "huis", "tijd", "dat", "deze", "dit", "zo", "zeer", "dus", "omdat", "toen", "eten", "drinken", "spreken"],
    },
    it: {
        strong: ["ciao", "grazie", "prego", "buongiorno", "buonasera", "buonanotte", "scusi", "scusa", "sono", "sì", "perché", "è", "anche", "molto", "bene", "italiano", "mio", "questo", "piace", "piacciono"],
        common: ["io", "lui", "lei", "noi", "voi", "loro", "sei", "siamo", "ho", "ha", "hai", "abbiamo", "il", "lo", "gli", "uno", "una", "da", "con", "su", "per", "tra", "fra", "ma", "ancora", "già", "male", "qui", "qua", "là", "oggi", "domani", "ieri", "adesso", "sempre", "mai", "quello", "tuo", "suo", "nostro", "chi", "come", "dove", "quando", "cosa", "quanto", "non", "più", "così", "mangiare", "bere", "parlare", "vorrei", "voglio", "della", "delle", "dello", "nella", "nelle", "alla", "alle", "agli", "dei", "sul", "sulla", "trova", "però", "tutti", "tutto", "ogni", "giorno", "giorni", "mangio"],
    },
};

const LATIN_LANGUAGES = Object.keys(LEXICON) as LatinLanguage[];

const WORD_WEIGHTS: Record<string, Partial<Scores>> = {};
LATIN_LANGUAGES.forEach((language) => {
    const add = (words: string[], weight: number) =>
        words.forEach((word) => {
            WORD_WEIGHTS[word] = { ...WORD_WEIGHTS[word], [language]: Math.max(weight, WORD_WEIGHTS[word]?.[language] ?? 0) };
        });
    add(LEXICON[language].common, COMMON);
    add(LEXICON[language].strong, STRONG);
});

function countMatches(text: string, pattern: RegExp): number {
    return (text.match(pattern) ?? []).length;
}

// How many letters of each writing system a text has. Shared with
// translateOutputCheck.ts, which judges a translation by the same script ranges
// this file uses to identify the language of what the learner typed.
export type ScriptCounts = Record<"kana" | "hangul" | "cyrillic" | "arabic" | "han" | "latin", number>;

export function scriptCounts(text: string): ScriptCounts {
    return {
        kana: countMatches(text, KANA),
        hangul: countMatches(text, HANGUL),
        cyrillic: countMatches(text, CYRILLIC),
        arabic: countMatches(text, ARABIC),
        han: countMatches(text, HAN),
        latin: countMatches(text, LATIN),
    };
}

function emptyScores(): Scores {
    return { en: 0, pt: 0, es: 0, fr: 0, de: 0, nl: 0, it: 0 };
}

function detectLatin(text: string): LatinLanguage | null {
    const lower = text.toLowerCase();
    const scores = emptyScores();
    const addAll = (weights: Partial<Scores> | undefined, times = 1) => {
        if (!weights) return;
        LATIN_LANGUAGES.forEach((language) => {
            scores[language] += (weights[language] ?? 0) * times;
        });
    };

    const characters = Array.from(lower);
    characters.forEach((character) => addAll(CHAR_HINTS[character]));

    (lower.match(LATIN_TOKEN) ?? []).forEach((token) => {
        addAll(WORD_WEIGHTS[token]);
        if (FRENCH_ELISION.test(token)) scores.fr += COMMON;
    });

    const ranked = LATIN_LANGUAGES.slice().sort((a, b) => scores[b] - scores[a]);
    const top = ranked[0];
    const lead = scores[top] - scores[ranked[1]];
    return scores[top] >= MIN_SCORE && lead >= MIN_LEAD ? top : null;
}

export type DetectOptions = {
    currentOther?: OtherLanguage;
    jaIsSource?: boolean;
};

// Best-effort language identification for the translator's two sides: Japanese
// plus the supported "other" languages. It returns null whenever the evidence
// is thin (a single shared word, mixed scripts, romaji, ...) so a wrong guess
// never overrides what the learner picked. Kana always means Japanese; the
// other non-Latin scripts map to one language each; Han-only text is Chinese
// only when it uses simplified-only characters or the learner is already on
// Chinese, and Japanese otherwise.
export function detectLanguage(text: string, options: DetectOptions = {}): DetectedLanguage | null {
    const counts = scriptCounts(text);
    const total = counts.kana + counts.hangul + counts.cyrillic + counts.arabic + counts.han + counts.latin;
    if (total === 0) return null;
    if (counts.kana > 0) return "ja";

    const scripts: Array<keyof typeof counts> = ["hangul", "cyrillic", "arabic", "han", "latin"];
    const dominant = scripts.reduce((best, script) => (counts[script] > counts[best] ? script : best), scripts[0]);
    if (counts[dominant] / total < 0.5) return null;

    if (dominant === "hangul") return "ko";
    if (dominant === "cyrillic") return "ru";
    if (dominant === "arabic") return "ar";
    if (dominant === "han") {
        const hasSimplified = Array.from(text).some((character) => SIMPLIFIED_ONLY.indexOf(character) !== -1);
        if (hasSimplified) return "zh";
        return options.currentOther === "zh" && options.jaIsSource === false ? "zh" : "ja";
    }
    return detectLatin(text);
}

export type DetectedDirection = { direction: Direction; language: DetectedLanguage };

// Returns the direction the learner most likely wants for what they typed, or
// null when the current one already fits (or nothing could be detected). The
// non-Japanese side of the pair follows the text; Japanese text only flips
// which side Japanese is on and keeps the chosen "other" language.
export function detectDirectionChange(text: string, current: Direction): DetectedDirection | null {
    const currentOther = otherLanguageOf(current);
    const jaIsSource = isJaSource(current);
    const language = detectLanguage(text, { currentOther, jaIsSource });
    if (!language) return null;

    if (language === "ja") {
        return jaIsSource ? null : { direction: directionFor(true, currentOther), language };
    }
    if (!jaIsSource && language === currentOther) return null;
    return { direction: directionFor(false, language), language };
}
